import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { StaffSideMenu } from "../../staff/StaffSideMenu";
import API_URL from "../../../api/API";
import { useStaffAuthContext } from "../../staff/useStaffAuthContext";

export const ExerciseEdit = () => {
  const { staff } = useStaffAuthContext();
  const { id, phase_id } = useParams(); // Get the :id from the URL
  const navigate = useNavigate(); // For navigation after editing
  const [title, setTitle] = useState(""); // State to store the title
  const [description, setDescription] = useState(""); // State to store the description
  const [video_url, setVideoUrl] = useState(""); // State to store the video URL
  const [status, setStatus] = useState("ACTIVE");

  const [isLoading, setIsLoading] = useState(false); // State to manage loading
  const [error, setError] = useState(""); // State to store error messages
  const [successMessage, setSuccessMessage] = useState(""); // State to store success message

  // Fetch the phase data by ID
  useEffect(() => {
    const fetchExercise = async () => {
      try {
        setIsLoading(true);
        setError("");
        const response = await fetch(`${API_URL.GET_PHASE_EXERCISE}/${id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${staff.token}`,
          },
        });
        const data = await response.json();

        if (!response.ok) {
          setError(data.error || "Failed to load exercise data");
        }

        setTitle(data.title);
        setDescription(data.description);
        setVideoUrl(data.video_url);
        setStatus(data.status);
        setIsLoading(false);
      } catch (err) {
        setError(err.message);
        setIsLoading(false);
      }
    };

    fetchExercise();
  }, [id, staff.token]);

  // Handle form submission to edit the phase
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsLoading(true);
      setError("");
      setSuccessMessage("");

      const response = await fetch(`${API_URL.PATCH_PHASE_EXERCISE_BY_ID}${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${staff.token}`,
        },
        body: JSON.stringify({ phase_id, title, description, video_url, status }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Failed to update exercise");
      }
      if (response.ok) {
        setSuccessMessage("Exercise updated successfully!");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-screen">
      <StaffSideMenu />

      {/* Main Content */}
      <div className="flex-1 p-6 bg-gray-100 pt-16">
        {/* Go Back Button */}
        <div className="absolute top-4 right-4">
          <button
            onClick={() => navigate(-1)} // Navigate back to the previous page
            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
          >
            Go Back
          </button>
        </div>

        <h1 className="text-2xl font-bold mb-6">Edit Exercise</h1>

        <form
          onSubmit={handleSubmit}
          className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4 max-w-lg mx-auto"
        >
          {/* Title Input */}
          <div className="mb-4">
            <label
              htmlFor="title"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Exercise Title*
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter exercise title"
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              required
            />
          </div>

          {/* Description Input */}
          <div className="mb-4">
            <label
              htmlFor="description"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Exercise Description*
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter exercise description"
              rows="4"
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              required
            />
          </div>

          {/* Video Input */}
          <div className="mb-4">
            <label
              htmlFor="video_url"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              YouTube Video URL
            </label>
            <input
              type="text"
              id="video_url"
              value={video_url}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="Enter YouTube embed URL"
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
          </div>

          {/* Status Input */}
          <div className="mb-4">
            <label
              htmlFor="status"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Status*
            </label>
            <input
              type="text"
              id="status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              placeholder="Status"
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
          </div>

          {/* Error Message */}
          {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

          {/* Success Message */}
          {successMessage && (
            <p className="text-green-500 text-sm mb-4">{successMessage}</p>
          )}

          {/* Submit Button */}
          <div className="flex items-center justify-between">
            <button
              type="submit"
              disabled={isLoading}
              className={`${
                isLoading ? "bg-blue-300" : "bg-blue-500 hover:bg-blue-700"
              } text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline`}
            >
              {isLoading ? "Editing..." : "Edit Exercise"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
